import React, { useEffect, useMemo, useState } from 'react';
import { Check, ClipboardList, X } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import productionRawMaterialsAPI from '../../services/productionRawMaterialsAPI';
import { formatDateEs, formatQty } from './productionDomain';
import { formatMoneyClp } from './rawMaterialsDomain';
import ProductionStatusChip from './ProductionStatusChip';

const COUNT_STATUS_COLORS = {
  Borrador: '#d97706',
  Confirmado: '#16a34a',
};

const monthInputFromPeriod = (period) => String(period || '').slice(0, 7);
const lastCompletedPeriod = () => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const lastDayOfPeriod = (period) => {
  const [year, month] = String(period || '').split('-').map(Number);
  if (!year || !month) return '';
  return new Date(year, month, 0).toISOString().slice(0, 10);
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  borderRadius: 8,
  border: 'none',
  background: '#16a34a',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

const ghostBtn = (ui) => ({
  ...primaryBtn,
  background: 'transparent',
  color: ui.text,
  border: `1px solid ${ui.border}`,
});

const inputStyle = (ui) => ({
  height: 36,
  borderRadius: 8,
  border: `1px solid ${ui.inputBorder}`,
  background: ui.inputBg,
  color: ui.text,
  padding: '0 10px',
  fontSize: 13,
  boxSizing: 'border-box',
});

const differenceColor = (value) => {
  const n = Number(value || 0);
  if (Math.abs(n) < 0.0001) return null;
  return n > 0 ? '#166534' : '#9a3412';
};

const RawMaterialsMonthEndInventoryModal = ({
  isOpen,
  countId = null,
  onClose,
  onSaved,
}) => {
  const ui = useUIColors();
  const [period, setPeriod] = useState(lastCompletedPeriod());
  const [count, setCount] = useState(null);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [countDate, setCountDate] = useState(lastDayOfPeriod(lastCompletedPeriod()));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const confirmed = count?.status === 'Confirmado';

  const loadCount = async (id) => {
    const payload = await productionRawMaterialsAPI.getInventoryCount(id);
    setCount(payload);
    setPeriod(payload.period);
    setCountDate(String(payload.count_date || '').slice(0, 10));
    setNotes(payload.notes || '');
    setItems(Array.isArray(payload.items) ? payload.items : []);
    return payload;
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setError('');
    setQuery('');
    if (countId) {
      setLoading(true);
      loadCount(countId)
        .catch((err) => {
          if (!cancelled) setError(err?.message || 'No se pudo cargar el inventario de cierre.');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else {
      const nextPeriod = lastCompletedPeriod();
      setCount(null);
      setItems([]);
      setPeriod(nextPeriod);
      setCountDate(lastDayOfPeriod(nextPeriod));
      setNotes('');
    }
    return () => {
      cancelled = true;
    };
  }, [isOpen, countId]);

  const visibleItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((row) =>
      [row.material_code, row.material_name, row.family, row.warehouse]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [items, query]);

  const totals = useMemo(() => {
    const theoretical = items.reduce((sum, row) => sum + Number(row.theoretical_qty || 0), 0);
    const counted = items.reduce((sum, row) => sum + Number(row.counted_qty || 0), 0);
    return {
      items: items.length,
      theoretical,
      counted,
      difference: counted - theoretical,
    };
  }, [items]);

  const updateCounted = (materialId, value) => {
    setItems((current) =>
      current.map((row) =>
        Number(row.material_id) === Number(materialId)
          ? { ...row, counted_qty: value === '' ? '' : Number(value) }
          : row
      )
    );
  };

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = await productionRawMaterialsAPI.createInventoryCount({
        period,
        count_date: countDate || lastDayOfPeriod(period),
        notes,
      });
      setCount(payload);
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setPeriod(payload.period);
      setCountDate(String(payload.count_date || '').slice(0, 10));
      setNotes(payload.notes || '');
      onSaved?.(payload);
    } catch (err) {
      setError(err?.message || 'No se pudo crear el inventario de cierre.');
    } finally {
      setSaving(false);
    }
  };

  const persist = async ({ confirm = false } = {}) => {
    if (!count?.id) return null;
    const payload = await productionRawMaterialsAPI.updateInventoryCount(count.id, {
      period: count.period,
      count_date: countDate,
      notes,
      items: items.map((row) => ({
        material_id: Number(row.material_id),
        counted_qty: Number(row.counted_qty || 0),
        warehouse: row.warehouse || '',
        notes: row.notes || '',
      })),
    });
    if (confirm) {
      return productionRawMaterialsAPI.confirmInventoryCount(count.id);
    }
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = await persist();
      setCount(payload);
      setItems(Array.isArray(payload.items) ? payload.items : []);
      onSaved?.(payload);
    } catch (err) {
      setError(err?.message || 'No se pudo guardar el inventario de cierre.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (
      !window.confirm(
        'Al confirmar, estas cantidades quedan como stock de apertura del mes siguiente y se registran los ajustes de inventario. ¿Continuar?'
      )
    ) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = await persist({ confirm: true });
      setCount(payload);
      setItems(Array.isArray(payload.items) ? payload.items : []);
      onSaved?.(payload);
    } catch (err) {
      setError(err?.message || 'No se pudo confirmar el inventario de cierre.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: ui.overlay || 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'min(1280px, calc(100vw - 24px))',
          maxHeight: '92vh',
          background: ui.page || ui.panel,
          border: `1px solid ${ui.border}`,
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          overflow: 'hidden',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <header style={{ padding: '16px 18px 12px', borderBottom: `1px solid ${ui.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: ui.text }}>
                Inventario de cierre de mes
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
                El conteo físico de fin de mes queda como punto de partida del mes siguiente.
              </p>
            </div>
            <button type="button" style={ghostBtn(ui)} onClick={onClose} aria-label="Cerrar">
              <X size={16} />
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 10,
              marginTop: 14,
            }}
          >
            <label style={{ display: 'grid', gap: 4, fontSize: 11, fontWeight: 600, color: ui.muted }}>
              Mes de cierre
              <input
                type="month"
                value={monthInputFromPeriod(period)}
                disabled={Boolean(count)}
                onChange={(event) => {
                  const next = event.target.value;
                  setPeriod(next);
                  setCountDate(lastDayOfPeriod(next));
                }}
                style={inputStyle(ui)}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 11, fontWeight: 600, color: ui.muted }}>
              Fecha del conteo
              <input
                type="date"
                value={countDate}
                disabled={confirmed}
                onChange={(event) => setCountDate(event.target.value)}
                style={inputStyle(ui)}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 11, fontWeight: 600, color: ui.muted }}>
              Notas
              <input
                value={notes}
                disabled={confirmed}
                placeholder="Observaciones del inventario"
                onChange={(event) => setNotes(event.target.value)}
                style={inputStyle(ui)}
              />
            </label>
            {count ? (
              <div style={{ display: 'grid', gap: 4, alignContent: 'end' }}>
                <ProductionStatusChip status={count.status} color={COUNT_STATUS_COLORS[count.status]} />
                <span style={{ fontSize: 12, color: ui.muted }}>
                  Apertura de {count.opening_period_label || 'mes siguiente'}
                </span>
              </div>
            ) : null}
          </div>
        </header>

        <div style={{ minHeight: 0, overflow: 'auto', padding: 16 }}>
          {error ? <p style={{ margin: '0 0 12px', color: '#dc2626', fontSize: 12 }}>{error}</p> : null}
          {loading ? (
            <p style={{ margin: 0, fontSize: 13, color: ui.muted }}>Cargando inventario…</p>
          ) : !count ? (
            <div
              style={{
                border: `1px dashed ${ui.border}`,
                borderRadius: 12,
                padding: 24,
                display: 'grid',
                gap: 10,
                justifyItems: 'start',
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: ui.text, fontWeight: 600 }}>
                Elige el mes cerrado para cargar el catálogo con el stock teórico.
              </p>
              <p style={{ margin: 0, fontSize: 12, color: ui.muted, maxWidth: 640, lineHeight: 1.45 }}>
                Si ya hay un inventario confirmado anterior, el teórico parte de ese conteo más ingresos y
                egresos del mes. Si es el primero, se usa el stock actual de planta.
              </p>
              <button type="button" style={primaryBtn} onClick={handleCreate} disabled={saving}>
                <ClipboardList size={14} />
                {saving ? 'Preparando…' : 'Cargar materias primas'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar código, nombre, categoría…"
                style={{ ...inputStyle(ui), width: '100%' }}
              />
              <div style={{ overflow: 'auto', maxHeight: '52vh', border: `1px solid ${ui.border}`, borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['Código', 'Materia prima', 'Unidad', 'Teórico', 'Contado', 'Diferencia', 'Valor'].map(
                        (header) => (
                          <th
                            key={header}
                            style={{
                              position: 'sticky',
                              top: 0,
                              background: ui.panelAlt || ui.panel,
                              textAlign: header === 'Código' || header === 'Materia prima' ? 'left' : 'right',
                              padding: '8px 10px',
                              color: ui.muted,
                              borderBottom: `1px solid ${ui.border}`,
                            }}
                          >
                            {header}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((row) => {
                      const counted = Number(row.counted_qty || 0);
                      const theoretical = Number(row.theoretical_qty || 0);
                      const difference = counted - theoretical;
                      const color = differenceColor(difference);
                      return (
                        <tr key={row.material_id}>
                          <td style={{ padding: '7px 10px', color: ui.text }}>
                            <strong>{row.material_code}</strong>
                          </td>
                          <td style={{ padding: '7px 10px', color: ui.text }}>
                            {row.material_name}
                            <div style={{ fontSize: 11, color: ui.muted }}>{row.family}</div>
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', color: ui.muted }}>{row.unit}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                            {formatQty(theoretical, row.unit)}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                            {confirmed ? (
                              formatQty(counted, row.unit)
                            ) : (
                              <input
                                type="number"
                                min="0"
                                step="any"
                                value={row.counted_qty}
                                onChange={(event) => updateCounted(row.material_id, event.target.value)}
                                style={{
                                  ...inputStyle(ui),
                                  height: 32,
                                  width: 110,
                                  textAlign: 'right',
                                }}
                              />
                            )}
                          </td>
                          <td
                            style={{
                              padding: '7px 10px',
                              textAlign: 'right',
                              fontWeight: 700,
                              color: color || ui.text,
                            }}
                          >
                            {difference > 0 ? '+' : ''}
                            {formatQty(difference, row.unit)}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                            {formatMoneyClp(counted * Number(row.unit_cost || 0))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <footer
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            alignItems: 'center',
            padding: '12px 18px',
            borderTop: `1px solid ${ui.border}`,
            background: ui.panelAlt || ui.panel,
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12 }}>
            <span style={{ color: ui.muted }}>{totals.items} SKU</span>
            <span style={{ color: ui.text, fontWeight: 700 }}>
              Teórico {formatQty(totals.theoretical)}
            </span>
            <span style={{ color: ui.text, fontWeight: 700 }}>
              Contado {formatQty(totals.counted)}
            </span>
            <span style={{ color: differenceColor(totals.difference) || ui.text, fontWeight: 700 }}>
              Diferencia {totals.difference > 0 ? '+' : ''}
              {formatQty(totals.difference)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={ghostBtn(ui)} onClick={onClose}>
              Cerrar
            </button>
            {count && !confirmed ? (
              <>
                <button type="button" style={ghostBtn(ui)} onClick={handleSave} disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar borrador'}
                </button>
                <button type="button" style={primaryBtn} onClick={handleConfirm} disabled={saving}>
                  <Check size={14} /> Confirmar inventario
                </button>
              </>
            ) : null}
          </div>
        </footer>
      </div>
    </div>
  );
};

export const inventoryCountStatusColor = COUNT_STATUS_COLORS;
export const formatInventoryPeriod = (count) =>
  count?.period_label || formatDateEs(count?.count_date) || count?.period || '—';

export default RawMaterialsMonthEndInventoryModal;
